import { useEffect, useMemo, useState } from "react";
import { DndContext, DragOverlay, useDroppable, useDraggable, PointerSensor, useSensor, useSensors, type DragEndEvent, type DragStartEvent } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { Users, Clock, ArrowRight, Sparkles } from "lucide-react";
import { type LeadStage } from "../../data/studio";
import { getStudioLeads, type DerivedLead } from "../../lib/studioFromGraph";
import { BrandIcon, type BrandSlug } from "../BrandIcon";
import { env } from "../../lib/env";
import { cls } from "../../lib/cls";
import { formatCompactMoney } from "../../lib/i18n/format";

/* Lead is now derived from PROJECTS + typed STUDIO_PROSPECTS at render
   time — every row is real, every date is today-relative, no SEED_LEADS
   literals. The pipeline is STUDIO-WIDE (not filtered by active project)
   so switching the ProjectTracker keeps the same kanban — that's correct
   architecture for a sales pipeline. */
type Lead = DerivedLead;

/* SOTA kanban via @dnd-kit. Drag-between-columns with smooth animation,
   keyboard-accessible, persists per-lead stage in localStorage. */

const STAGES: { id: LeadStage; label: string; tone: string }[] = [
  { id: "new",       label: "New",       tone: "from-rose-50/60    via-cream to-cream" },
  { id: "qualified", label: "Qualified", tone: "from-sky-50/60     via-cream to-cream" },
  { id: "proposal",  label: "Proposal",  tone: "from-amber-50/60   via-cream to-cream" },
  { id: "won",       label: "Won",       tone: "from-emerald-50/60 via-cream to-cream" },
  { id: "lost",      label: "Lost",      tone: "from-slate-100/60  via-cream to-cream" },
];

const TIER_DOT: Record<"standard" | "premium" | "luxury", string> = {
  standard: "bg-slate-300",
  premium:  "bg-foyr",
  luxury:   "bg-violet-500",
};

const STAGE_KEY = `${env.VITE_DEMO_SEED.split("-")[0] ?? "hgm"}.studio.leadStage.v1`;

function loadOverrides(): Record<string, LeadStage> {
  try {
    const raw = localStorage.getItem(STAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, LeadStage>;
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}
function saveOverrides(o: Record<string, LeadStage>): void {
  try { localStorage.setItem(STAGE_KEY, JSON.stringify(o)); } catch { /* ignore */ }
}

export function LeadsKanban(): JSX.Element {
  const [overrides, setOverrides] = useState<Record<string, LeadStage>>(() => loadOverrides());
  const [activeId, setActiveId] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  // Re-render on project change so today-relative dates stay current
  // (the rows themselves are studio-wide so they don't filter, but the
  // computed `aged_days` rendering should refresh).
  useEffect(() => {
    const on = (): void => setTick((t) => t + 1);
    window.addEventListener("foyrmind:projectChanged", on);
    return () => window.removeEventListener("foyrmind:projectChanged", on);
  }, []);
  const leads = useMemo(() => getStudioLeads(), [tick]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const stageOf = (lead: Lead): LeadStage => overrides[lead.id] ?? lead.stage;

  function onDragStart(ev: DragStartEvent): void {
    setActiveId(String(ev.active.id));
  }
  function onDragEnd(ev: DragEndEvent): void {
    setActiveId(null);
    const overId = ev.over?.id;
    if (!overId) return;
    const next = String(overId) as LeadStage;
    if (!STAGES.some((s) => s.id === next)) return;
    const id = String(ev.active.id);
    const updated = { ...overrides, [id]: next };
    setOverrides(updated);
    saveOverrides(updated);
  }

  const active = useMemo(() => leads.find((l) => l.id === activeId), [leads, activeId]);

  return (
    <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
      <div className="mb-3 inline-flex items-center gap-1.5 pill border border-border bg-cream-dark/40 text-[10.5px] text-ink-500">
        <Users className="w-3 h-3" />
        <span className="text-ink-700 font-medium">Studio pipeline · across all projects</span>
        <span className="text-ink-400">·</span>
        <span>
          {leads.length} rows derived live · won/lost from PROJECTS · new/qualified from prospects
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {STAGES.map((s) => {
          const colLeads = leads.filter((l) => stageOf(l) === s.id);
          const totalInr = colLeads.reduce((a, l) => a + l.value_inr, 0);
          return (
            <Column key={s.id} stage={s.id} label={s.label} tone={s.tone} count={colLeads.length} totalInr={totalInr}>
              {colLeads.map((l) => <LeadCard key={l.id} lead={l} />)}
            </Column>
          );
        })}
      </div>
      <DragOverlay dropAnimation={{ duration: 160 }}>
        {active ? (
          <div className="rounded-xl border border-foyr/40 p-2.5 bg-cream shadow-[0_8px_24px_-6px_rgba(0,0,0,0.20)] w-[280px] rotate-[1.5deg] cursor-grabbing">
            <div className="flex items-center justify-between gap-1.5">
              <div className="text-[12.5px] font-semibold text-ink-950 truncate">{active.name}</div>
              <span className={cls("w-2 h-2 rounded-full shrink-0", TIER_DOT[active.budget_tier])} />
            </div>
            <div className="text-[11px] text-ink-700 mt-1.5 leading-snug">{active.enquiry}</div>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

// fmtInr name preserved for callers; body delegates to country-aware formatter.
function fmtInr(n: number): string {
  return formatCompactMoney(n);
}

function Column({ stage, label, tone, children, count, totalInr }: { stage: LeadStage; label: string; tone: string; children: React.ReactNode; count: number; totalInr: number }): JSX.Element {
  const { setNodeRef, isOver } = useDroppable({ id: stage });
  return (
    <div
      ref={setNodeRef}
      className={cls(
        "rounded-2xl border bg-gradient-to-b shadow-[0_1px_2px_rgba(0,0,0,0.03),0_8px_20px_-12px_rgba(0,0,0,0.08)] p-2.5 min-h-[260px] transition-colors flex flex-col",
        tone,
        isOver ? "border-foyr/60 ring-2 ring-foyr/20" : "border-border/60",
      )}
    >
      <div className="flex items-baseline justify-between mb-2.5 px-1">
        <div className="font-newyork text-[12.5px] uppercase tracking-[0.18em] text-ink-700 font-semibold">{label}</div>
        <div className="text-[10px] tabular text-ink-500">{count}</div>
      </div>
      <div className="space-y-2 min-h-[80px] flex-1">{children}</div>
      {/* Column footer — pipeline value aggregate derived live. */}
      <div className="mt-2.5 pt-2 border-t border-border/40 flex items-center justify-between px-1">
        <span className="text-[9.5px] uppercase tracking-wider text-ink-500">pipeline</span>
        <span className="font-newyork text-[12px] font-semibold text-ink-950 tabular">{count > 0 ? fmtInr(totalInr) : "—"}</span>
      </div>
    </div>
  );
}

function LeadCard({ lead }: { lead: Lead }): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: lead.id });
  const style: React.CSSProperties = {
    transform: transform ? CSS.Translate.toString(transform) : undefined,
    opacity: isDragging ? 0.4 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={cls(
        "rounded-xl border border-border/70 p-2.5 bg-cream shadow-[0_1px_2px_rgba(0,0,0,0.04)] cursor-grab hover:shadow-[0_4px_12px_-4px_rgba(0,0,0,0.08)] hover:-translate-y-0.5 transition-all select-none",
        isDragging && "cursor-grabbing",
      )}
    >
      {/* ─── Top row · brand icon + name + tier dot ─── */}
      <div className="flex items-start gap-2">
        <span
          className="grid place-items-center w-7 h-7 rounded-lg bg-cream-dark/60 border border-border/60 shrink-0"
          title={`Source: ${lead.source_app}`}
        >
          <BrandIcon slug={lead.source_app as BrandSlug} size={14} title={lead.source_app} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-1.5">
            <div className="font-newyork text-[16px] font-semibold text-ink-950 leading-tight truncate tracking-tight">{lead.name}</div>
            <span className={cls("w-2 h-2 rounded-full shrink-0", TIER_DOT[lead.budget_tier])} title={`${lead.budget_tier} tier`} />
          </div>
          <div className="font-newyork italic text-[11.5px] text-ink-500 leading-tight">{lead.city}</div>
        </div>
      </div>

      {/* ─── Enquiry summary ─── */}
      <div className="text-[11.5px] text-ink-700 mt-2 leading-snug">{lead.enquiry}</div>

      {/* ─── Meta strip · value · age · tier ─── */}
      <div className="flex items-center gap-1.5 flex-wrap mt-2">
        <span className="pill text-[9.5px] border border-foyr/30 bg-foyr/5 text-foyr-deep tabular font-mono">
          {fmtInr(lead.value_inr)}
        </span>
        <span className="pill text-[9.5px] border border-border bg-cream-dark text-ink-700 inline-flex items-center gap-1">
          <Clock className="w-2.5 h-2.5" />
          {lead.age_days === 0 ? "today" : `${lead.age_days}d`}
        </span>
        <span className="pill text-[9.5px] border border-border bg-cream-dark text-ink-500 uppercase tracking-wider">
          {lead.budget_tier}
        </span>
      </div>

      {/* ─── Next-best action — derived from agent intelligence ─── */}
      <div className="mt-2.5 rounded-lg border border-foyr/20 bg-foyr/5 px-2 py-1.5 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[9px] uppercase tracking-wider text-foyr-deep/80 inline-flex items-center gap-1">
            <Sparkles className="w-2.5 h-2.5" /> next
          </div>
          <div className="text-[11.5px] text-ink-950 font-medium leading-tight truncate" title={lead.next_action.label}>
            {lead.next_action.label}
          </div>
        </div>
        <ArrowRight className="w-3 h-3 text-foyr shrink-0" />
      </div>
      {lead.next_action.agent !== "—" ? (
        <div className="mt-1 text-[9px] font-mono text-ink-400 truncate" title={`MCP tool: ${lead.next_action.agent}`}>
          via {lead.next_action.agent}
        </div>
      ) : null}

      {/* ─── Why · typed graph reason ─── */}
      <div
        className="mt-2 pt-2 border-t border-border/50 text-[10px] text-ink-600 leading-snug"
        title={`Source: ${lead.reason.source}\nCites: ${lead.reason.cited_nodes.join(", ")}`}
      >
        <span className="uppercase tracking-wider text-[9px] text-ink-400 mr-1">why</span>
        {lead.reason.why}
      </div>

      {/* ─── Date footer ─── */}
      <div className="text-[9.5px] text-ink-400 tabular mt-1.5 inline-flex items-center gap-1">
        <span className="uppercase tracking-wider">first touch</span>
        <span>·</span>
        {new Date(lead.created).toLocaleDateString()}
      </div>
    </div>
  );
}
